import { SxProps, Theme } from "@mui/material";

// ============================================
// STATUS CONFIGURATION - Single Source of Truth
// ============================================

export interface IStatusOption {
  label: string;
  value: string;
  variant: string;
  sx?: SxProps<Theme>;
}

export interface IStatusCount {
  status: string;
  cnt: number;
}

// All status options in display order
export const STATUS_OPTIONS: IStatusOption[] = [
  {
    label: "Initiate",
    value: "INITIATE",
    variant: "warning",
  },
  {
    label: "Sent",
    value: "SENT",
    variant: "info",
  },
  {
    label: "Delivered",
    value: "DELIVERED",
    variant: "secondary",
  },
  {
    label: "Read",
    value: "READ",
    variant: "success",
  },
  {
    label: "Replied",
    value: "RECEIVED",
    variant: "greenish",
  },
  {
    label: "Failed",
    value: "FAILED",
    variant: "error",
  },
  {
    label: "Number Invalid",
    value: "INVALID_NUMBER",
    variant: "primary",
  },
  {
    label: "Block Marketing",
    value: "BLOCKED_MARKETING",
    variant: "red",
    sx: {
      backgroundColor: "#ffe9e9 !important",
      color: "#ff5959 !important",
      borderColor: "#ff5959 !important",
    },
  },
];

// Status labels for charts (bar graph and pie chart) - ordered for visual consistency
export const CHART_STATUS_LABELS = [
  { value: "INITIATE", label: "Initiated", variant: "warning" },
  { value: "FAILED", label: "Failed", variant: "error" },
  { value: "INVALID_NUMBER", label: "Number Invalid", variant: "warning" },
  { value: "READ", label: "Read", variant: "success" },
  { value: "SENT", label: "Sent", variant: "info" },
  { value: "DELIVERED", label: "Delivered", variant: "secondary" },
  { value: "RECEIVED", label: "Replied", variant: "info" },
  { value: "BLOCKED_MARKETING", label: "Blocked Marketing", variant: "error" },
];

// Chart colors in order matching CHART_STATUS_LABELS
export const getChartColors = (theme: any) => [
  theme.palette.warning.main, // INITIATE
  theme.palette.error.main, // FAILED
  theme.palette.primary.main, // INVALID_NUMBER
  theme.palette.success.main, // READ
  theme.palette.info.main, // SENT
  theme.palette.secondary.main, // DELIVERED
  "#49c7cc", // RECEIVED
  "#ff5959", // BLOCKED_MARKETING
];

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get list of known status values (lowercase for comparison)
 */
export const getKnownStatuses = (): string[] => {
  return STATUS_OPTIONS.map((opt) => opt.value.toLowerCase());
};

/**
 * Process whatsapp counts - groups unknown statuses under FAILED
 * This ensures consistency across tabs, charts, and other components
 */
export const processWhatsappCounts = (
  whatsappCounts: IStatusCount[],
): IStatusCount[] => {
  const knownStatuses = getKnownStatuses();

  // Calculate the sum of all unknown status counts (to be grouped under FAILED)
  const unknownStatusCount = whatsappCounts
    .filter(
      (countObj) =>
        !knownStatuses.includes((countObj.status || "").toLowerCase()),
    )
    .reduce((sum, countObj) => sum + (countObj.cnt || 0), 0);

  // Create a modified whatsappCounts array with unknown status counts added to FAILED
  const modifiedWhatsappCounts = whatsappCounts.map((countObj) => {
    if ((countObj.status || "").toLowerCase() === "failed") {
      return {
        ...countObj,
        cnt: (countObj.cnt || 0) + unknownStatusCount,
      };
    }
    return countObj;
  });

  // If FAILED status doesn't exist in whatsappCounts but there are unknown statuses, add it
  const hasFailedStatus = whatsappCounts.some(
    (countObj) => (countObj.status || "").toLowerCase() === "failed",
  );

  if (!hasFailedStatus && unknownStatusCount > 0) {
    modifiedWhatsappCounts.push({
      status: "FAILED",
      cnt: unknownStatusCount,
    });
  }

  return modifiedWhatsappCounts;
};

/**
 * Get status option by value
 */
export const getStatusOption = (value: string): IStatusOption | undefined => {
  return STATUS_OPTIONS.find(
    (status) => status.value.toLowerCase() === value.toLowerCase(),
  );
};

/**
 * Transform status counts to chart data format
 */
export const transformToChartData = (
  statusCounts: IStatusCount[],
  chartColors: string[],
) => {
  // Process counts to group unknown statuses under FAILED
  const processedCounts = processWhatsappCounts(statusCounts);

  // Create a map of status counts for easy lookup
  const statusCountsMap = new Map<string, number>();
  processedCounts?.forEach((item) => {
    statusCountsMap.set(item.status, item.cnt);
  });

  // Use all status labels and get counts from the map (default to 0 if not present)
  const categories = CHART_STATUS_LABELS.map((item) => item.label);
  const seriesData = CHART_STATUS_LABELS.map(
    (item) => statusCountsMap.get(item.value) || 0,
  );

  return {
    categories,
    seriesData,
    // For pie chart
    pieData: {
      series: seriesData,
      labels: categories,
      colors: chartColors,
    },
    // For bar chart
    barData: {
      categories,
      series: [
        {
          name: "Message Status",
          data: seriesData,
        },
      ],
    },
  };
};
